import { useEffect, useState, useCallback, useRef } from "react";
import { INTRO_SCENES } from "../lib/helpContent";
import { X, ChevronRight, ChevronLeft, Pause, Play, ArrowRight } from "lucide-react";

const SCENE_MS = 6500;

export default function IntroStory({ open, onClose, onPrimaryCta, autoAdvance = true, primaryCtaLabel, autoToVideoMs = null }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [viewersLast7d, setViewersLast7d] = useState(null);
  const timerRef = useRef(null);
  const lastIdxRef = useRef(0);
  const completedFiredRef = useRef(false);

  const scene = INTRO_SCENES[idx];
  const isLast = idx === INTRO_SCENES.length - 1;

  // Fire-and-forget analytics beacon — mirrors FeatureVideo's instrumentation
  // but carries `source: "intro"` so stats can be filtered per leg of the
  // greeting. Keepalive ensures `closed` survives page teardown.
  const fireEvent = useCallback((event, sceneIdx, sceneKey) => {
    try {
      fetch("/api/public/feature-video/event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          event,
          scene_idx: sceneIdx,
          scene_key: sceneKey,
          time_s: 0,
          source: "intro",
        }),
      }).catch(() => {});
    } catch {
      /* noop */
    }
  }, []);

  const next = useCallback(() => {
    setIdx((i) => {
      if (i < INTRO_SCENES.length - 1) {
        fireEvent("scene_end", i, INTRO_SCENES[i]?.key);
        lastIdxRef.current = i + 1;
        return i + 1;
      }
      return i;
    });
  }, [fireEvent]);
  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

  // Reset on open — fire started + fetch intro-specific viewer count.
  useEffect(() => {
    if (open) {
      setIdx(0);
      setPaused(false);
      lastIdxRef.current = 0;
      completedFiredRef.current = false;
      fireEvent("started", 0, INTRO_SCENES[0]?.key);
      fetch("/api/public/feature-video/stats?source=intro")
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d && typeof d.viewers_last_7d === "number") setViewersLast7d(d.viewers_last_7d);
        })
        .catch(() => {});
    }
  }, [open, fireEvent]);

  // Fire `completed` once when the user reaches the last scene (CTA visible).
  useEffect(() => {
    if (!open) return;
    if (isLast && !completedFiredRef.current) {
      fireEvent("scene_end", INTRO_SCENES.length - 1, scene?.key);
      fireEvent("completed", INTRO_SCENES.length - 1, scene?.key);
      completedFiredRef.current = true;
    }
  }, [open, isLast, scene, fireEvent]);

  // Wrap onClose to log a `closed` event with the user's current scene.
  const handleClose = useCallback(() => {
    if (!completedFiredRef.current) {
      fireEvent("closed", lastIdxRef.current, INTRO_SCENES[lastIdxRef.current]?.key);
    }
    onClose?.();
  }, [fireEvent, onClose]);

  // Auto-advance
  useEffect(() => {
    if (!open || paused || !autoAdvance || isLast) return undefined;
    timerRef.current = setTimeout(next, SCENE_MS);
    return () => clearTimeout(timerRef.current);
  }, [open, idx, paused, autoAdvance, isLast, next]);

  // Seamless hand-off: once the viewer reaches the final scene, roll straight
  // into the feature film after a short beat — no clicking the CTA. Paused
  // viewers are respected (timer is cleared), and the manual CTA stays as a
  // fallback / early exit.
  useEffect(() => {
    if (!open || paused || !isLast || !autoToVideoMs || !onPrimaryCta) return undefined;
    const id = setTimeout(() => onPrimaryCta(), autoToVideoMs);
    return () => clearTimeout(id);
  }, [open, paused, isLast, autoToVideoMs, onPrimaryCta]);

  // Keyboard
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === " ") {
        e.preventDefault();
        setPaused((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, handleClose]);

  if (!open || !scene) return null;

  const isDark = scene.tone === "dark";
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90"
      data-testid="intro-story"
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`relative w-full h-full ${
          isDark ? "bg-neutral-950 text-white" : "bg-[#FAFAF7] text-neutral-900"
        } transition-colors duration-700`}
      >
        {/* Background grain */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' /></svg>\")",
          }}
        />
        {/* Accent diagonal */}
        <div
          className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full blur-[120px] opacity-30 pointer-events-none transition-colors duration-700"
          style={{ backgroundColor: scene.accent }}
        />

        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 px-6 lg:px-12 py-6 flex items-center justify-between z-10">
          <div className={`flex items-center gap-3 ${isDark ? "" : ""}`}>
            <img
              src="/logo-mark.png"
              alt="Three Little Birds"
              className={`h-9 w-auto ${isDark ? "" : ""}`}
              draggable={false}
            />
            <div className="font-display font-black text-base tracking-tighter">
              THREE LITTLE BIRDS
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              data-testid="intro-pause-btn"
              aria-label={paused ? "Play" : "Pause"}
              className={`w-9 h-9 flex items-center justify-center border ${
                isDark
                  ? "border-white/20 hover:border-white/60 text-white"
                  : "border-neutral-300 hover:border-neutral-900 text-neutral-900"
              }`}
            >
              {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={handleClose}
              data-testid="intro-skip-btn"
              className={`text-[10px] uppercase tracking-[0.25em] font-bold px-3 h-9 flex items-center gap-1.5 border ${
                isDark
                  ? "border-white/20 hover:bg-white hover:text-neutral-950 text-white"
                  : "border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white text-neutral-900"
              }`}
            >
              Skip & sign in
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Scene content — keyed for re-mount animation. Uses min-h-full +
            top/bottom padding so it never collides with the absolutely-
            positioned top bar (~80px) or bottom controls bar (~80px). On
            short viewports the content scrolls instead of getting hidden
            under the bottom bar — fixes the unclickable-CTA bug. */}
        <div className="relative z-[5] h-full overflow-y-auto px-6 lg:px-16 pt-24 pb-28 flex items-center justify-center">
          <div
            key={idx}
            className="max-w-4xl w-full intro-fade-in my-auto"
            data-testid={`intro-scene-${idx}`}
          >
            <div
              className="text-[10px] uppercase tracking-[0.4em] mb-6 font-bold"
              style={{ color: scene.accent }}
            >
              {scene.eyebrow}
            </div>

            {isLast && viewersLast7d != null && viewersLast7d >= 5 && (
              <div
                className="mb-6 inline-flex items-center gap-2 bg-[#7C5FB0]/90 text-white text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 backdrop-blur font-bold"
                data-testid="intro-story-viewer-count"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                {viewersLast7d.toLocaleString()} founders
                <span className="opacity-80">watched this week</span>
              </div>
            )}

            <h2
              className="font-display font-black tracking-tighter leading-[0.92] whitespace-pre-line text-5xl sm:text-6xl lg:text-8xl"
              style={{ textWrap: "balance" }}
            >
              {scene.headline}
            </h2>

            <p
              className={`mt-8 text-lg lg:text-xl leading-relaxed max-w-2xl ${
                isDark ? "text-neutral-300" : "text-neutral-700"
              }`}
            >
              {scene.body}
            </p>

            {scene.stats && (
              <div className="mt-10 grid grid-cols-3 gap-0 border max-w-2xl"
                style={{ borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.16)" }}>
                {scene.stats.map((s, i) => (
                  <div
                    key={`stat-${s.label}-${i}`}
                    className={`p-5 ${i > 0 ? "border-l" : ""}`}
                    style={{ borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.16)" }}
                  >
                    <div
                      className="font-display font-black text-3xl sm:text-4xl tracking-tighter"
                      style={{ color: scene.accent }}
                    >
                      {s.n}
                    </div>
                    <div className={`text-[10px] uppercase tracking-[0.2em] mt-2 ${isDark ? "text-neutral-400" : "text-neutral-500"}`}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {scene.chips && (
              <div className="mt-8 flex flex-wrap gap-2 max-w-3xl">
                {scene.chips.map((c, i) => (
                  <span
                    key={`chip-${c}-${i}`}
                    className={`text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 border intro-chip-in ${
                      isDark
                        ? "border-white/30 text-white"
                        : "border-neutral-900 text-neutral-900"
                    }`}
                    style={{ animationDelay: `${i * 90}ms` }}
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}

            {scene.cta && (
              <div className="mt-12 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => onPrimaryCta?.()}
                  data-testid="intro-cta-btn"
                  className="h-14 px-8 inline-flex items-center gap-2 bg-[#7C5FB0] hover:bg-[#5E4689] text-white font-bold tracking-wide text-base group"
                >
                  {primaryCtaLabel || "Sign in & start your plan"}
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </button>
                <button
                  type="button"
                  onClick={() => setIdx(0)}
                  data-testid="intro-replay-btn"
                  className={`h-14 px-6 inline-flex items-center gap-2 border-2 font-bold tracking-wide text-sm uppercase ${
                    isDark
                      ? "border-white/30 hover:border-white text-white"
                      : "border-neutral-900 hover:bg-neutral-900 hover:text-white text-neutral-900"
                  }`}
                >
                  Replay
                </button>
              </div>
            )}

            {scene.cta && autoToVideoMs && !paused && (
              <div
                className={`mt-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] font-bold ${isDark ? "text-neutral-400" : "text-neutral-500"}`}
                data-testid="intro-autoadvance-hint"
              >
                <span className="relative inline-block w-20 h-[3px] overflow-hidden"
                  style={{ backgroundColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.14)" }}>
                  <span
                    className="absolute left-0 top-0 h-full"
                    style={{ backgroundColor: scene.accent, animation: `introAutoBar ${autoToVideoMs}ms linear both` }}
                  />
                </span>
                The feature film starts in a moment…
              </div>
            )}
          </div>
        </div>

        {/* Bottom controls */}
        <div className="absolute bottom-0 left-0 right-0 px-6 lg:px-12 py-6 flex items-center gap-4 z-10">
          <button
            type="button"
            onClick={prev}
            disabled={idx === 0}
            data-testid="intro-prev-btn"
            aria-label="Previous"
            className={`w-10 h-10 flex items-center justify-center border disabled:opacity-30 disabled:cursor-not-allowed ${
              isDark
                ? "border-white/20 hover:border-white/60 text-white"
                : "border-neutral-300 hover:border-neutral-900 text-neutral-900"
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Progress segments */}
          <div className="flex-1 flex gap-1.5" data-testid="intro-progress">
            {INTRO_SCENES.map((s, i) => (
              <div
                key={`prog-${s?.key ?? i}`}
                className={`h-[3px] flex-1 overflow-hidden ${
                  isDark ? "bg-white/15" : "bg-neutral-300"
                }`}
              >
                <div
                  className="h-full"
                  style={{
                    backgroundColor: scene.accent,
                    width:
                      i < idx
                        ? "100%"
                        : i === idx
                        ? paused
                          ? "20%"
                          : "100%"
                        : "0%",
                    transition:
                      i === idx
                        ? `width ${paused ? "0ms" : SCENE_MS + "ms"} linear`
                        : "width 200ms",
                  }}
                />
              </div>
            ))}
          </div>

          <div
            className={`text-[10px] uppercase tracking-[0.25em] font-bold ${
              isDark ? "text-neutral-400" : "text-neutral-500"
            }`}
          >
            {String(idx + 1).padStart(2, "0")} /{" "}
            {String(INTRO_SCENES.length).padStart(2, "0")}
          </div>

          <button
            type="button"
            onClick={next}
            disabled={isLast}
            data-testid="intro-next-btn"
            aria-label="Next"
            className={`w-10 h-10 flex items-center justify-center border disabled:opacity-30 disabled:cursor-not-allowed ${
              isDark
                ? "border-white/20 hover:border-white/60 text-white"
                : "border-neutral-300 hover:border-neutral-900 text-neutral-900"
            }`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes introFadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .intro-fade-in { animation: introFadeIn 700ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        @keyframes introChipIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .intro-chip-in { animation: introChipIn 500ms ease-out both; }
        @keyframes introAutoBar { from { width: 0%; } to { width: 100%; } }
      `}</style>
    </div>
  );
}
